import React, { useState, useEffect } from 'react';
import Header from './Header';
import './scss/Payments.scss';
import AddCashModal from './components/AddCashModal';
import ConfirmDialog from './components/ConfirmDialog';
import api from './services/apiService';
import UnreconciledTransactions from './components/payments/UnreconciledTransactions';
import CSVColumnMapper from './components/payments/CSVColumnMapper';
import TransactionMatcher from './components/payments/TransactionMatcher';

function Payments() {
  const [file, setFile] = useState(null);
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'owing', 'credit'
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [unreconciledEntries, setUnreconciledEntries] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState(null);
  const [columnMapping, setColumnMapping] = useState(null);
  const [csvData, setCsvData] = useState(null);

  useEffect(() => {
    const teamId = sessionStorage.getItem('selected_team_id');
    if (teamId) {
      fetchPlayerData(teamId);
    }
  }, []);

  const fetchPlayerData = async (teamId) => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/process_payments.php?team_id=${teamId}`);
      const data = response.data;
      if (data.success) {
        setPlayerData(data);
      } else {
        setError(data.error || 'Failed to fetch player data');
      }
    } catch (error) {
      console.error('Error fetching player data:', error);
      setError('An error occurred while fetching player data.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setFile(file);
    
    if (file) {
      try {
        const formData = new FormData();
        formData.append('csv_file', file);
        formData.append('get_headers', 'true');

        const response = await api.uploadFile(
          `${process.env.REACT_APP_API_URL}/process_payments.php?team_id=${sessionStorage.getItem('selected_team_id')}`,
          formData
        );

        if (response.data.success && response.data.headers) {
          setCsvHeaders(response.data.headers);
        } else {
          setError('Failed to read CSV headers');
        }
      } catch (error) {
        console.error('Error reading CSV headers:', error);
        setError('Error reading CSV file');
      }
    }
  };

  const handleColumnMappingNext = async (mapping) => {
    console.log('Column mapping:', mapping);
    setLoading(true);
    setError(null);

    try {
        const formData = new FormData();
        formData.append('csv_file', file);
        formData.append('column_mapping', JSON.stringify(mapping));
        formData.append('get_csv_data', 'true'); // New flag to just get data

        const response = await api.uploadFile(
            `${process.env.REACT_APP_API_URL}/process_payments.php?team_id=${sessionStorage.getItem('selected_team_id')}`,
            formData
        );

        if (response.data.success) {
            setCsvData(response.data);
            setColumnMapping(mapping);
        } else {
            setError(response.data.error || 'Failed to read CSV data');
        }
    } catch (error) {
        console.error('Error reading CSV:', error);
        setError('An error occurred while reading the CSV file.');
    } finally {
        setLoading(false);
    }
  };

  const handleSaveMatches = async (matchedTransactions) => {
    setLoading(true);
    setError(null);

    try {
        console.log('Sending matched transactions:', matchedTransactions);

        const response = await api.post(
            `${process.env.REACT_APP_API_URL}/process_payments.php?team_id=${sessionStorage.getItem('selected_team_id')}`,
            {
                type: 'process_matches',
                matches: matchedTransactions
            },
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Response:', response.data);

        if (response.data.success) {
            await fetchPlayerData(sessionStorage.getItem('selected_team_id'));
            setSuccessMessage(response.data.message);
            
            // Clear CSV related states
            setFile(null);
            setCsvHeaders(null);
            setColumnMapping(null);
            setCsvData(null);

            if (response.data.errors && response.data.errors.length > 0) {
                setError(response.data.errors.join('\n'));
            }
        } else {
            setError(response.data.error || 'Failed to process transactions');
        }
    } catch (error) {
        console.error('Error processing transactions:', error);
        setError('An error occurred while processing the transactions.');
    } finally {
        setLoading(false);
    }
  };

  const getFilteredPlayers = () => {
    if (!playerData || !playerData.players) return [];
    
    return playerData.players.filter(player => {
      const balance = Number(player.summary.current_balance);
      switch (filter) {
        case 'owing':
          return balance < 0;
        case 'credit':
          return balance > 0;
        default:
          return true;
      }
    });
  };

  const handleAddCash = async (paymentData) => {
    const teamId = sessionStorage.getItem('selected_team_id');
    setLoading(true);
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/process_payments.php?team_id=${teamId}`, {
        type: 'cash',
        teamId: teamId,
        ...paymentData
      });

      const result = response.data;
      if (result.success) {
        setSuccessMessage(result.message || 'Payment added successfully');
        setSelectedPlayer(null);
        
        setTimeout(() => {
          fetchPlayerData(teamId);
          setSuccessMessage('');
        }, 2000);
      } else {
        setError(result.error || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('An error occurred while processing the payment.');
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalCashClick = () => {
    if (selectedPlayerId) {
      const player = playerData.players.find(p => p.id === selectedPlayerId);
      if (player) {
        setSelectedPlayer(player);
      }
    }
  };

  const handleDeleteTransaction = async (playerId, transactionId) => {
    const teamId = sessionStorage.getItem('selected_team_id');
    setLoading(true);
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/process_payments.php?team_id=${teamId}`, {
        type: 'delete_transaction',
        playerId,
        transactionId,
        teamId: teamId
      });

      const result = response.data;
      if (result.success) {
        setSuccessMessage(result.message || 'Transaction deleted successfully');
        setTimeout(() => {
          fetchPlayerData(teamId);
          setSuccessMessage('');
        }, 2000);
      } else {
        setError(result.error || 'Failed to delete transaction');
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setError('An error occurred while deleting the transaction.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (player, transaction) => {
    setDeleteConfirm({
      player,
      transaction,
      message: `Are you sure you want to delete the payment of £${Number(transaction.amount).toFixed(2)} for ${player.name}?`
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      await handleDeleteTransaction(
        deleteConfirm.player.id, 
        deleteConfirm.transaction.id
      );
      setDeleteConfirm(null);
    }
  };

  if (loading && !playerData) {
    return <div className="Payments container">Loading...</div>;
  }

  return (
    <div className="Payments container">
      <Header />
      <div>
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        <h1>Balance Payments</h1>
      </div>

      <div className="upload-section">
        <div className="upload-container">
          <h3>Upload CSV File</h3>
          {!csvHeaders ? (
            <form onSubmit={(e) => e.preventDefault()}>
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleFileChange} 
              />
            </form>
          ) : csvData ? (
            <TransactionMatcher 
              csvData={csvData.csv_data}
              players={csvData.players}
              onSave={handleSaveMatches}
            />
          ) : (
            <CSVColumnMapper 
              headers={csvHeaders}
              file={file}
              onNext={handleColumnMappingNext}
              onCancel={() => {
                setCsvHeaders(null);
                setFile(null);
              }}
            />
          )}
          {error && <p className="error">{error}</p>}
        </div>

        {unreconciledEntries.length > 0 && playerData && (
          <UnreconciledTransactions 
            entries={unreconciledEntries} 
            players={playerData.players}
            onUpdate={() => fetchPlayerData(sessionStorage.getItem('selected_team_id'))}
            className="upload-section-unreconciled"
          />
        )}
      </div>

      {playerData && (
        <div className="global-actions">
          <div className="player-selector">
            <select
              value={selectedPlayerId}
              onChange={(e) => setSelectedPlayerId(e.target.value)}
              className="player-select"
            >
              <option value="">Select a player...</option>
              {playerData.players.map(player => (
                <option key={player.id} value={player.id}>
                  {player.name}
                </option>
              ))}
            </select>
            <button 
              className="add-cash-button"
              onClick={handleGlobalCashClick}
              disabled={!selectedPlayerId}
            >
              + Add Cash Payment
            </button>
          </div>
        </div>
      )}

      {playerData && (
        <div className="summary-card">
          <h2>Overall Summary</h2>
          <div className="summary-stats">
            <div className="stat-item clickable" onClick={() => setFilter('all')}>
              <span>Total Players</span>
              <span>{playerData.summary.total_players}</span>
            </div>
            <div className="stat-item clickable" onClick={() => setFilter('owing')}>
              <span>Players Owing</span>
              <span className="amount-owed">{playerData.summary.players_owing}</span>
            </div>
            <div className="stat-item clickable" onClick={() => setFilter('credit')}>
              <span>Players in Credit</span>
              <span className="balance-positive">{playerData.summary.players_in_credit}</span>
            </div>
            <div className="stat-item">
              <span>Total Payments</span>
              <span>£{playerData.summary.total_payments}</span>
            </div>
            <div className="stat-item">
              <span>Total Owed</span>
              <span className="amount-owed">£{playerData.summary.total_owed}</span>
            </div>
            <div className="stat-item">
              <span>Total Games (2025)</span>
              <span>{playerData.summary.total_games_played}</span>
            </div>
          </div>
          
          <div className="filter-controls">
            <button 
              className={filter === 'all' ? 'active' : ''} 
              onClick={() => setFilter('all')}
            >
              All Players ({playerData.summary.total_players})
            </button>
            <button 
              className={filter === 'owing' ? 'active' : ''} 
              onClick={() => setFilter('owing')}
            >
              Players Owing ({playerData.summary.players_owing})
            </button>
            <button 
              className={filter === 'credit' ? 'active' : ''} 
              onClick={() => setFilter('credit')}
            >
              Players in Credit ({playerData.summary.players_in_credit})
            </button>
          </div>
        </div>
      )}

      {playerData && getFilteredPlayers().map((player) => (
        <div key={player.id} className="player-card">
          <div className="player-header">
            <h2>{player.name}</h2>
            <div className="player-summary">
              <span>Total Games: {player.summary.total_games}</span>
              <span>Total Cost: £{Number(player.summary.total_cost).toFixed(2)}</span>
              <span>Total Paid: £{Number(player.summary.total_payments).toFixed(2)}</span>
              <span className={Number(player.summary.current_balance) >= 0 ? 'balance-positive' : 'amount-owed'}>
                Balance: £{Number(player.summary.current_balance).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="matches-grid">
            <h3>Games Played</h3>
            <div className="grid-header">
              <span>Date</span>
              <span>Team</span>
              <span>Cost</span>
            </div>
            {player.games.map((game, index) => (
              <div key={index} className="match-row">
                <span>{new Date(game.match_date).toLocaleDateString()}</span>
                <span>{game.colour}</span>
                <span>£5.00</span>
              </div>
            ))}
          </div>

          <div className="matches-grid">
            <h3>Transactions</h3>
            <div className="grid-header">
              <span>Date</span>
              <span>Amount</span>
              <span>Status</span>
              <span>Action</span>
            </div>
            {player.transactions.map((transaction, index) => (
              <div key={transaction.id || index} className="match-row">
                <span>{new Date(transaction.date).toLocaleDateString()}</span>
                <span>£{Number(transaction.amount).toFixed(2)}</span>
                <span className={`payment-status ${transaction.status}`}>
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </span>
                <span>
                  {transaction.description?.startsWith('CASH PAYMENT') && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteClick(player, transaction)}
                      title="Delete transaction"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {selectedPlayer && (
        <AddCashModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          onSubmit={handleAddCash}
        />
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDialog
          message={deleteConfirm.message}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

export default Payments; 